// Eigen omgeving voor leden en ouders (stap G), plus het claimen van een
// persoonsrecord via een uitnodigingslink.
//
// Een aparte applicatie in dezelfde bundel: andere navigatie, andere rechten,
// veel minder scherm. Deelt alleen lib/, ui/ en data/ met het beheerscherm.
// Wat hier te zien is, bepaalt de database: public.mijn_gegevens() en de
// policies voor de persoon zelf en zijn ouders.
import { t, locale, vertaalDom, koppelTaalkeuze } from '../lib/i18n.js';
import { esc, fmtMoney } from '../lib/format.js';
import { toast } from '../ui/toast.js';
import { showConfirm } from '../ui/confirm.js';
import { inschrijvingStatus } from '../lib/money.js';
import { toonDossier } from '../ui/dossier.js';
import { toonVerzoeken } from '../ui/verzoeken.js';
import { toonLidmaatschap } from '../ui/lidmaatschap.js';

const DOELEN_TOESTEMMING = ['beeldmateriaal', 'mailing_extern', 'publicatie_uitslagen'];
const PERSOONSVELDEN = ['voornaam', 'tussenvoegsel', 'achternaam', 'email', 'telefoon', 'straat', 'huisnummer', 'postcode', 'plaats', 'land'];

let sb = null;
let gegevens = [];
let activiteiten = [];       // uit mijn_activiteiten(): per persoon en activiteit
let terugNaarBeheer = null;

// ── Claimtoken in de adresbalk ─────────────────────────────────────────────
// Zelfde aanpak als de uitnodigingen voor beheerders: bewaren in
// sessionStorage, omdat de omleiding na het inloggen de adresbalk leegmaakt.
export function bewaarClaimToken() {
  const uitUrl = new URLSearchParams(location.search).get('claim');
  if (uitUrl) { try { sessionStorage.setItem('pending_claim', uitUrl); } catch { /* geen opslag */ } }
}
export function claimToken() {
  const uitUrl = new URLSearchParams(location.search).get('claim');
  if (uitUrl) return uitUrl;
  try { return sessionStorage.getItem('pending_claim') || ''; } catch { return ''; }
}
function wisClaimToken() { try { sessionStorage.removeItem('pending_claim'); } catch { /* geen opslag */ } }

// Foutcodes uit de database (claim:<code>) naar tekst.
export function claimFout(e) {
  const m = String(e?.message || e || '').match(/claim:([a-z_0-9]+)/);
  return m ? t('claim.fout.' + m[1]) : t('alg.fout', { melding: e?.message || String(e) });
}

function scherm() {
  let el = document.getElementById('mijnRoot');
  if (!el) {
    el = document.createElement('div');
    el.id = 'mijnRoot';
    el.className = 'mijn-root';
    document.body.appendChild(el);
    el.addEventListener('click', klik);
    el.addEventListener('submit', e => e.preventDefault());
    el.addEventListener('change', e => { if (e.target.matches('select[data-taalkeuze]')) teken(); });
  }
  for (const id of ['authScreen', 'deniedScreen', 'inviteScreen']) document.getElementById(id)?.classList.remove('active');
  const app = document.getElementById('appRoot');
  if (app) app.style.display = 'none';
  el.style.display = 'block';
  document.body.classList.remove('laden');
  return el;
}

// ── Claimen ────────────────────────────────────────────────────────────────
export async function toonClaim(client, email) {
  sb = client;
  const el = scherm();
  el.innerHTML = `<div class="mijn-kaart"><div class="spinner"></div></div>`;
  const { data: info, error } = await sb.rpc('claim_info', { p_token: claimToken() });
  if (error || !info) { toonClaimFout(el, error); return; }
  const kop = info.kind === 'ouder' ? t('claim.titel.ouder', { naam: info.voornaam }) : t('claim.titel.zelf');
  let inhoud;
  if (info.status !== 'open') {
    inhoud = `<div class="banner banner-amber"><i class="ti ti-alert-triangle"></i><span>${esc(t('claim.status.' + info.status))}</span></div>`;
  } else if (!info.email_klopt) {
    inhoud = `<div class="banner banner-amber"><i class="ti ti-mail-exclamation"></i><span>${esc(t('claim.anderemail', { email: info.email, ingelogd: email }))}</span></div>
      <button class="btn btn-default" data-actie="uitloggen" style="width:100%;justify-content:center"><i class="ti ti-logout"></i> ${esc(t('claim.anderaccount'))}</button>`;
  } else {
    inhoud = `<p class="mijn-tekst">${esc(t(info.kind === 'ouder' ? 'claim.uitleg.ouder' : 'claim.uitleg.zelf', { organisatie: info.organisatie, naam: info.voornaam }))}</p>
      <button class="btn btn-primary" data-actie="claim" style="width:100%;justify-content:center"><i class="ti ti-user-check"></i> ${esc(t('claim.bevestig'))}</button>`;
  }
  el.innerHTML = `<div class="mijn-kaart">
      <h2>${esc(kop)}</h2>
      <p class="mijn-sub">${esc(info.organisatie || '')}</p>
      ${inhoud}
      <div id="claimMelding"></div>
      ${taalkeuze()}
    </div>`;
  koppelTaalkeuze(el);
}

function toonClaimFout(el, e) {
  el.innerHTML = `<div class="mijn-kaart"><div class="banner banner-red"><i class="ti ti-alert-circle"></i><span>${esc(claimFout(e))}</span></div>
    <button class="btn btn-default" data-actie="uitloggen"><i class="ti ti-logout"></i> ${esc(t('auth.uitloggen'))}</button></div>`;
}

async function claim() {
  const { error } = await sb.rpc('claim_accepteren', { p_token: claimToken() });
  if (error) {
    document.getElementById('claimMelding').innerHTML =
      `<div class="banner banner-red" style="margin-top:12px"><i class="ti ti-alert-circle"></i><span>${esc(claimFout(error))}</span></div>`;
    return;
  }
  wisClaimToken();
  location.replace(location.pathname);
}

// ── Eigen omgeving ─────────────────────────────────────────────────────────
// Geeft true als er iets te tonen is. opties.terug: functie om naar het
// beheerscherm te gaan, voor wie ook beheerder is.
export async function laadEigenGegevens(client) {
  sb = client;
  const [g, a] = await Promise.all([sb.rpc('mijn_gegevens'), sb.rpc('mijn_activiteiten')]);
  gegevens = g.error ? [] : (g.data || []);
  // Zonder de module activiteiten (of een oudere database) blijft dit leeg.
  activiteiten = a.error ? [] : (a.data || []);
  return gegevens.length > 0;
}

export async function start(client, opties = {}) {
  sb = client;
  terugNaarBeheer = opties.terug || null;
  if (!gegevens.length && !(await laadEigenGegevens(client))) return false;
  teken();
  return true;
}

function taalkeuze() {
  return `<div class="taal-keuze" style="justify-content:center;margin-top:16px"><i class="ti ti-language"></i><select data-taalkeuze aria-label="Taal / Language"></select></div>`;
}

function datum(iso) {
  if (!iso) return '';
  return new Date(iso + 'T00:00:00Z').toLocaleDateString(locale(), { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });
}

function teken() {
  const el = scherm();
  el.innerHTML = `
    <div class="mijn-kop">
      <div><strong>${esc(t('mijn.titel'))}</strong></div>
      <div style="display:flex;gap:8px;align-items:center">
        ${terugNaarBeheer ? `<button class="btn btn-ghost btn-sm" data-actie="beheer"><i class="ti ti-arrow-left"></i> ${esc(t('mijn.naarbeheer'))}</button>` : ''}
        <div class="taal-keuze" style="margin:0"><i class="ti ti-language"></i><select data-taalkeuze aria-label="Taal / Language"></select></div>
        <button class="btn btn-ghost btn-sm" data-actie="uitloggen"><i class="ti ti-logout"></i> ${esc(t('auth.uitloggen'))}</button>
      </div>
    </div>
    <div class="mijn-inhoud">
      <p class="mijn-tekst">${esc(t('mijn.uitleg'))}</p>
      ${gegevens.map((g, i) => kaart(g, i) + activiteitenKaart(g) + `<div class="mijn-kaart breed" data-dossiervan="${esc(g.persoon.id)}"></div>`).join('')}
      <div class="mijn-kaart breed" id="mijnLidmaatschap" hidden></div>
      <div class="mijn-kaart breed" id="mijnVerzoeken"></div>
    </div>`;
  koppelTaalkeuze(el);
  vertaalDom(el);
  el.querySelectorAll('[data-dossiervan]').forEach(d => toonDossier(d, sb, d.dataset.dossiervan));
  const lidm = el.querySelector('#mijnLidmaatschap');
  if (lidm) toonLidmaatschap(lidm, sb);
  verzoekenPaneel(el);
}

// Verzoeken van het lid aan bond of club. Het paneel blijft verborgen zolang er
// niets te tonen is en er ook niets geopend kan worden.
function verzoekenPaneel(el) {
  const doel = el.querySelector('#mijnVerzoeken');
  if (!doel) return;
  const client = sb;
  toonVerzoeken(doel, client, {
    schrijven: true,
    metBond: true,
    verbergLeeg: true,
    zichtbaarheidKeuze: false,
    uitleg: 'verz.uitleg.lid',
    openen: async () => {
      const uit = [];
      for (const g of gegevens) {
        const p = g.persoon;
        const naam = [p.voornaam, p.tussenvoegsel, p.achternaam].filter(Boolean).join(' ');
        const { data, error } = await client.rpc('case_lid_opties', { p_person: p.id });
        if (error) continue;
        for (const o of data || []) {
          uit.push({ label: gegevens.length > 1 ? `${o.bond_naam} (${naam})` : o.bond_naam,
            args: { p_bond: o.bond_id, p_club_org: o.club_org_id || null, p_person: p.id, p_affiliation: o.affiliation_id || null } });
        }
      }
      return uit;
    },
  });
}

function kaart(g, index) {
  const p = g.persoon;
  const naam = [p.voornaam, p.tussenvoegsel, p.achternaam].filter(Boolean).join(' ');
  const lid = (g.lidmaatschappen || []).find(l => l.status === 'actief');
  const velden = PERSOONSVELDEN.map(v => `<div class="form-group"><label class="form-label">${esc(t('leden.veld.' + v))}</label>
      <input class="form-input" name="${v}" value="${esc(p[v] || '')}"${v === 'email' ? ' type="email"' : ''}></div>`).join('');
  const ouders = (g.ouders || []).map(o => {
    const kanVerlengen = g.als === 'zelf' && g.leeftijd >= 17;
    return `<div class="org-row">
      <div style="flex:1;min-width:180px"><div style="font-size:13px">${esc(o.naam || o.email || '')} <span style="color:var(--text-3)">${esc(t('leden.ouder.soort.' + o.kind))}</span></div>
        <div style="font-size:11px;color:var(--text-3)">${o.ends_on ? esc(t('mijn.ouder.tot', { datum: datum(o.ends_on) })) : ''}${o.geclaimd ? '' : ' &middot; ' + esc(t('mijn.ouder.nietgeclaimd'))}</div></div>
      ${kanVerlengen ? `<button type="button" class="btn btn-default btn-sm" data-actie="verleng" data-id="${o.id}" data-index="${index}"><i class="ti ti-calendar-plus"></i> ${esc(t('mijn.ouder.verleng'))}</button>
        <button type="button" class="btn btn-ghost btn-sm" data-actie="beeindig" data-id="${o.id}"><i class="ti ti-user-minus"></i> ${esc(t('mijn.ouder.beeindig'))}</button>` : ''}
    </div>`;
  }).join('');
  const toestemming = DOELEN_TOESTEMMING.map(d => `<label style="display:flex;align-items:center;gap:8px;font-size:13px;margin-bottom:6px">
      <input type="checkbox" name="toestemming_${d}" ${g.toestemmingen?.[d] ? 'checked' : ''}> ${esc(t('leden.toestemming.' + d))}</label>`).join('');
  return `<form class="mijn-kaart breed" data-index="${index}" autocomplete="off">
    <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:10px;flex-wrap:wrap">
      <div><h3 style="margin:0">${esc(naam)}</h3>
        <div class="mijn-sub">${esc(g.organisatie || '')}${g.als === 'ouder' ? ' &middot; ' + esc(t('mijn.alsouder')) : ''}</div></div>
      ${lid ? `<span class="badge badge-green">${esc(t('mijn.lidsinds', { datum: datum(lid.starts_on) || '?' }))}</span>` : ''}
    </div>
    <div class="section-title" style="margin-top:14px"><i class="ti ti-user"></i> ${esc(t('mijn.sectie.gegevens'))}</div>
    <div class="form-row">${velden}</div>
    <div style="font-size:12px;color:var(--text-3);margin-bottom:12px"><i class="ti ti-lock" style="font-size:11px"></i>
      ${esc(t('mijn.vast', { geboortedatum: p.geboortedatum ? datum(p.geboortedatum) : t('alg.onbekend') }))}</div>
    <div class="section-title"><i class="ti ti-shield-check"></i> ${esc(t('leden.sectie.toestemming'))}</div>
    <div style="font-size:12px;color:var(--text-3);margin-bottom:8px">${esc(t('mijn.toestemming.uitleg'))}</div>
    ${toestemming}
    ${ouders ? `<div class="section-title" style="margin-top:14px"><i class="ti ti-users"></i> ${esc(t(g.als === 'zelf' ? 'mijn.sectie.ouders' : 'mijn.sectie.jouwrelatie'))}</div>
      ${g.als === 'zelf' && g.leeftijd >= 17 ? `<div style="font-size:12px;color:var(--text-3);margin-bottom:8px">${esc(t('mijn.ouder.uitleg'))}</div>` : ''}
      ${ouders}` : ''}
    <div style="display:flex;justify-content:flex-end;margin-top:14px">
      <button type="button" class="btn btn-primary" data-actie="opslaan" data-index="${index}"><i class="ti ti-device-floppy"></i> ${esc(t('alg.opslaan'))}</button>
    </div>
  </form>`;
}

// ── Activiteiten: zelf inschrijven, aanvragen, afmelden ────────────────────
const geld = n => `€ ${fmtMoney(n)}`;
const BETAALKLEUR = { paid: 'badge-green', partial: 'badge-amber', unpaid: 'badge-red', nvt: 'badge-grey' };
const STATUSKLEUR = { aangevraagd: 'badge-amber', ingeschreven: 'badge-green', afgewezen: 'badge-red', afgemeld: 'badge-grey' };

function periode(a) {
  if (!a.start_datum) return '';
  return a.eind_datum && a.eind_datum !== a.start_datum ? `${datum(a.start_datum)} - ${datum(a.eind_datum)}` : datum(a.start_datum);
}

function activiteitRij(x) {
  const a = x.activiteit;
  const r = x.inschrijving;
  const lopend = r && (r.status === 'aangevraagd' || r.status === 'ingeschreven');
  const regels = [t('act.soort.' + a.kind), periode(a), a.locatie].filter(Boolean).join(' · ');
  let rechts = '';
  if (lopend) {
    rechts = r.kan_afmelden
      ? `<button type="button" class="btn btn-ghost btn-sm" data-actie="zelfafmelden" data-id="${esc(r.id)}" data-naam="${esc(a.naam)}"><i class="ti ti-user-minus"></i> ${esc(t(r.status === 'aangevraagd' ? 'mijn.act.intrekken' : 'act.afmelden'))}</button>`
      : `<span style="font-size:12px;color:var(--text-3)">${esc(t('mijn.act.afmeldenviaclub'))}</span>`;
  } else if (!x.bezwaar) {
    rechts = `<button type="button" class="btn btn-primary btn-sm" data-actie="zelfinschrijven" data-activiteit="${esc(a.id)}" data-persoon="${esc(x.person_id)}"><i class="ti ti-user-plus"></i> ${esc(t(a.akkoord_nodig ? 'mijn.act.aanvragen' : 'mijn.act.inschrijven'))}</button>`;
  } else if (!r || r.status === 'afgemeld' || r.status === 'afgewezen') {
    rechts = `<span style="font-size:12px;color:var(--text-3)">${esc(t('inschrijving.fout.' + x.bezwaar))}</span>`;
  }
  const status = r ? ` <span class="badge ${STATUSKLEUR[r.status] || 'badge-grey'}">${esc(t('mijn.act.status.' + r.status))}</span>` : '';
  let geldregel = '';
  if (r && r.status === 'ingeschreven' && Number(r.entry_fee_eur) > 0) {
    const s = inschrijvingStatus(r);
    geldregel = `<div style="font-size:12px;margin-top:4px">${esc(t('mijn.act.bedrag', { tarief: geld(r.entry_fee_eur), open: geld(r.balance_eur) }))}
      <span class="badge ${BETAALKLEUR[s] || 'badge-grey'}">${esc(r.settlement === 'written_off' ? t('act.betaal.afgeboekt') : t('act.betaal.' + s))}</span></div>`;
  } else if (!lopend && Number(a.tarief_eur) > 0) {
    geldregel = `<div style="font-size:12px;color:var(--text-3);margin-top:4px">${esc(t('mijn.act.tarief', { tarief: geld(a.tarief_eur) }))}</div>`;
  }
  const toelichting = [
    r?.status === 'aangevraagd' ? t('mijn.act.wachtopclub') : '',
    r?.status === 'afgewezen' && r.besluit_reden ? t('mijn.act.reden', { reden: r.besluit_reden }) : '',
    !lopend && !x.bezwaar && a.akkoord_nodig ? t('mijn.act.akkoorduitleg') : '',
    !lopend && !x.bezwaar && a.inschrijven_tot ? t('act.eis.tot', { datum: datum(a.inschrijven_tot) }) : '',
  ].filter(Boolean);
  return `<div class="org-row" data-mijnact="${esc(a.id)}" style="flex-wrap:wrap;gap:8px">
    <div style="flex:1;min-width:200px">
      <div style="font-size:13px"><strong>${esc(a.naam)}</strong>${status}</div>
      <div style="font-size:12px;color:var(--text-2)">${esc(regels)}</div>
      ${a.omschrijving ? `<div style="font-size:12px;color:var(--text-3)">${esc(a.omschrijving)}</div>` : ''}
      ${geldregel}
      ${toelichting.length ? `<div style="font-size:12px;color:var(--text-3);margin-top:2px">${esc(toelichting.join(' · '))}</div>` : ''}
    </div>
    <div>${rechts}</div>
  </div>`;
}

function activiteitenKaart(g) {
  const eigen = activiteiten.filter(x => x.person_id === g.persoon.id);
  if (!eigen.length) return '';
  return `<div class="mijn-kaart breed">
    <div class="section-title" style="margin-top:0"><i class="ti ti-calendar-event"></i> ${esc(t('mijn.act.titel', { naam: g.persoon.voornaam || g.persoon.achternaam || '' }))}</div>
    ${eigen.map(activiteitRij).join('')}
  </div>`;
}

function actFout(e) {
  const m = String(e?.message || e || '').match(/inschrijving:([a-z_]+)/);
  return m ? t('inschrijving.fout.' + m[1]) : claimFout(e);
}

async function zelfInschrijven(activiteit, persoon) {
  const x = activiteiten.find(y => y.activiteit.id === activiteit && y.person_id === persoon);
  if (!x) return;
  const g = gegevens.find(y => y.persoon.id === persoon);
  const naam = [g?.persoon.voornaam, g?.persoon.tussenvoegsel, g?.persoon.achternaam].filter(Boolean).join(' ');
  const a = x.activiteit;
  const vraag = [
    t(a.akkoord_nodig ? 'mijn.act.vraag.aanvraag' : 'mijn.act.vraag.inschrijving', { naam, activiteit: a.naam }),
    Number(a.tarief_eur) > 0 ? t('mijn.act.vraag.kosten', { tarief: geld(a.tarief_eur) }) : '',
  ].filter(Boolean).join(' ');
  showConfirm(t(a.akkoord_nodig ? 'mijn.act.aanvragen' : 'mijn.act.inschrijven'), vraag, async () => {
    const { data, error } = await sb.rpc('zelf_inschrijven', { p_activiteit: activiteit, p_persoon: persoon });
    if (error) { toast(actFout(error), 'error'); return; }
    toast(t(data?.status === 'aangevraagd' ? 'mijn.act.aangevraagd' : 'mijn.act.ingeschreven'), 'success');
    await laadEigenGegevens(sb);
    teken();
  });
}

async function zelfAfmelden(id, naam) {
  showConfirm(t('act.afmelden'), t('mijn.act.vraag.afmelden', { activiteit: naam }), async () => {
    const { error } = await sb.rpc('zelf_afmelden', { p_inschrijving: id });
    if (error) { toast(actFout(error), 'error'); return; }
    toast(t('act.afgemeld.gedaan'), 'success');
    await laadEigenGegevens(sb);
    teken();
  });
}

async function opslaan(index) {
  const g = gegevens[index];
  const form = document.querySelector(`#mijnRoot form[data-index="${index}"]`);
  const velden = {};
  for (const v of PERSOONSVELDEN) {
    const w = form.elements[v]?.value.trim() || null;
    if ((g.persoon[v] || null) !== w) velden[v] = v === 'email' && w ? w.toLowerCase() : w;
  }
  try {
    if (Object.keys(velden).length) {
      const { error } = await sb.from('persons').update(velden).eq('id', g.persoon.id);
      if (error) throw error;
    }
    const nieuw = DOELEN_TOESTEMMING
      .filter(d => !!g.toestemmingen?.[d] !== form.elements['toestemming_' + d].checked)
      .map(d => ({ person_id: g.persoon.id, organisation_id: g.persoon.organisation_id, purpose: d,
                   granted: form.elements['toestemming_' + d].checked, source: 'zelf' }));
    if (nieuw.length) {
      const { error } = await sb.from('consents').insert(nieuw);
      if (error) throw error;
    }
    toast(t('leden.opgeslagen'), 'success');
    await laadEigenGegevens(sb);
    teken();
  } catch (e) { toast(claimFout(e), 'error'); }
}

async function wijzigOuder(id, tot) {
  const { error } = await sb.rpc('ouder_toegang_wijzigen', { p_guardianship: id, p_tot: tot });
  if (error) { toast(claimFout(error), 'error'); return; }
  toast(t(tot ? 'mijn.ouder.verlengd' : 'mijn.ouder.beeindigd'), 'success');
  await laadEigenGegevens(sb);
  teken();
}

async function klik(e) {
  const knop = e.target.closest('[data-actie]');
  if (!knop) return;
  const actie = knop.dataset.actie;
  if (actie === 'uitloggen') { await sb?.auth.signOut(); location.replace(location.pathname); }
  if (actie === 'claim') await claim();
  if (actie === 'beheer' && terugNaarBeheer) {
    document.getElementById('mijnRoot').style.display = 'none';
    terugNaarBeheer();
  }
  if (actie === 'opslaan') await opslaan(Number(knop.dataset.index));
  if (actie === 'zelfinschrijven') await zelfInschrijven(knop.dataset.activiteit, knop.dataset.persoon);
  if (actie === 'zelfafmelden') await zelfAfmelden(knop.dataset.id, knop.dataset.naam);
  if (actie === 'verleng') {
    const tot = new Date(); tot.setUTCFullYear(tot.getUTCFullYear() + 1);
    const g = gegevens[Number(knop.dataset.index)];
    const geboren = g?.persoon.geboortedatum ? new Date(g.persoon.geboortedatum + 'T00:00:00Z') : null;
    // Een jaar na de achttiende verjaardag, of een jaar vanaf nu als die al voorbij is.
    if (geboren) {
      const achttien = new Date(geboren); achttien.setUTCFullYear(achttien.getUTCFullYear() + 19);
      if (achttien > tot) tot.setTime(achttien.getTime());
    }
    await wijzigOuder(knop.dataset.id, tot.toISOString().slice(0, 10));
  }
  if (actie === 'beeindig') {
    const id = knop.dataset.id;
    showConfirm(t('mijn.ouder.beeindig'), t('mijn.ouder.beeindigvraag'), () => wijzigOuder(id, null));
  }
}
